import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux"; // Force refresh v3
import {
  createSupportVideo,
  deleteSupportVideo,
  fetchSupportVideos,
  SupportVideo,
  SupportVideoType,
  uploadSupportVideo,
} from "../../../services/support-service";
import { BASE_API_URL } from "../../../config/api";
import { SnackbarContext } from "../../../config/hooks/use-toast";
import { RootState } from "../../../redux/store/store";
import {
  Button,
  Modal,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlayCircle,
  faLink,
  faVideo,
  faPlus,
  faTrash,
  faTimes,
  faEnvelope,
  faPhone,
  faHeadset,
  faQuestionCircle,
  faExternalLinkAlt,
  faSyncAlt,
  faTrashCan
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faYoutube,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";

const extractYouTubeId = (url: string): string | null => {
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const isYouTubeUrl = (url: string) => !!extractYouTubeId(url);

const buildStreamUrl = (url: string): string => {
  if (!url || isYouTubeUrl(url)) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const pathname = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
    return `${BASE_API_URL}local-storage/stream-video?fileName=${encodeURIComponent(pathname)}`;
  } catch {
    const sanitized = url.replace(/^\/+/, "");
    return `${BASE_API_URL}local-storage/stream-video?fileName=${encodeURIComponent(sanitized)}`;
  }
};

type EnrichedVideo = SupportVideo & { ytId: string | null; thumbnail?: string; videoSrc?: string };
const VIDEO_TYPE_LABELS: Record<SupportVideoType, string> = {
  SUPPORT: "Tutoriels",
  TESTIMONIAL: "Temoignages Landing",
};

const SupportPage: React.FC = () => {
  const snackbar = useContext(SnackbarContext);
  const roleId = useSelector(
    (state: RootState) => state?.user?.userData?.role?.id,
  );
  const roleName = useSelector(
    (state: RootState) => state?.user?.userData?.role?.name,
  );
  const isAdmin = roleId === 1 && roleName === "ROLE_ADMIN";

  const [videos, setVideos] = useState<SupportVideo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedVideo, setSelectedVideo] = useState<EnrichedVideo | null>(null);
  const [form, setForm] = useState({ title: "", youtubeUrl: "" });
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [activeType, setActiveType] = useState<SupportVideoType>("SUPPORT");
  const [saving, setSaving] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<number | null>(null);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);
        const data = await fetchSupportVideos(activeType);
        setVideos(data || []);
      } catch (error: any) {
        snackbar?.showMessage?.("Erreur", "Impossible de récupérer les tutoriels.", "error");
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, [activeType]);

  useEffect(() => {
    setForm({ title: "", youtubeUrl: "" });
    setSelectedVideoFile(null);
  }, [activeType]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isAdmin) return;

    const title = form.title.trim();
    const url = form.youtubeUrl.trim();

    if (!title) {
      snackbar?.showMessage?.("Atttention", "Veuillez entrer un titre.", "warning");
      return;
    }

    if (activeType === "SUPPORT") {
      if (!url) {
        snackbar?.showMessage?.("Atttention", "Veuillez renseigner le lien YouTube.", "warning");
        return;
      }
      const youtubeId = extractYouTubeId(url);
      if (!youtubeId) {
        snackbar?.showMessage?.("Lien invalide", "Veuillez entrer un lien YouTube valide.", "warning");
        return;
      }
    } else {
      if (!selectedVideoFile) {
        snackbar?.showMessage?.("Atttention", "Veuillez choisir un fichier video.", "warning");
        return;
      }
      if (selectedVideoFile.type && !selectedVideoFile.type.startsWith("video/")) {
        snackbar?.showMessage?.("Format invalide", "Le fichier selectionne doit etre une video.", "warning");
        return;
      }
    }

    try {
      setSaving(true);
      const created =
        activeType === "SUPPORT"
          ? await createSupportVideo(title, url, activeType)
          : await uploadSupportVideo(title, selectedVideoFile as File, activeType);
      setVideos((prev) => [created, ...prev]);
      setForm({ title: "", youtubeUrl: "" });
      setSelectedVideoFile(null);
      setShowAddModal(false);
      snackbar?.showMessage?.("Succes", "Video ajoutee avec succes.", "success");
    } catch (error: any) {
      snackbar?.showMessage?.("Erreur", "Action impossible.", "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (videoId: number) => {
    setVideoToDelete(videoId);
    setOpenDeleteAlert(true);
  };

  const handleCloseDeleteAlert = () => {
    setOpenDeleteAlert(false);
    setVideoToDelete(null);
  };

  const handleDelete = async () => {
    if (!isAdmin || videoToDelete === null) return;
    const videoId = videoToDelete;
    setDeletingId(videoId);
    try {
      await deleteSupportVideo(videoId);
      setVideos((prev) => prev.filter((video) => video.id !== videoId));
      snackbar?.showMessage?.("Succès", "Vidéo supprimée.", "success");
      handleCloseDeleteAlert();
    } catch (error: any) {
      snackbar?.showMessage?.("Erreur", "Suppression échouée.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const videoGrid = useMemo(
    () =>
      videos.map((video) => {
        const ytId = extractYouTubeId(video.youtubeUrl);
        const thumbnail = ytId
          ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`
          : undefined;
        const videoSrc = ytId ? undefined : buildStreamUrl(video.youtubeUrl);
        return { ...video, ytId, thumbnail, videoSrc };
      }),
    [videos],
  );

  return (
    <div className="min-h-screen bg-[#F8FAFB] p-4 lg:p-10">
      <div className="max-w-[1400px] mx-auto">
        
        {/* Cinematic Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-16">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex items-center gap-3 mb-2">
               <div className="w-8 h-1 bg-primary rounded-full"></div>
               <span className="text-[10px] font-black text-primary uppercase tracking-[0.4em]">Centre d'Aide</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-title tracking-tighter uppercase italic leading-none">
              Tutoriels <span className="text-primary italic">& Support</span>
            </h1>
            <p className="text-[11px] font-bold text-text/40 uppercase tracking-widest mt-4 ml-1 max-w-xl leading-relaxed">
              Explorez nos guides vidéo complets pour maîtriser chaque facette <br className="hidden md:block" /> de votre écosystème FOK NJE7EK.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden md:flex items-center gap-6 bg-white p-6 rounded-[2.5rem] shadow-sm border border-gray-100"
          >
             <div className="w-16 h-16 rounded-2xl bg-[#05322B] text-emerald-400 flex items-center justify-center text-2xl shadow-xl">
                <FontAwesomeIcon icon={faHeadset} />
             </div>
             <div>
                <p className="text-[11px] font-black text-title uppercase tracking-widest leading-none mb-1">Disponibilité</p>
                <p className="text-[20px] font-black text-primary uppercase tracking-tighter italic">24h/24 & 7j/7</p>
             </div>
          </motion.div>
        </div>

        {isAdmin && (
          <div className="mb-10 flex flex-wrap items-center gap-3">
            {(["SUPPORT", "TESTIMONIAL"] as SupportVideoType[]).map((videoType) => (
              <button
                key={videoType}
                type="button"
                onClick={() => setActiveType(videoType)}
                className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-[0.18em] border transition-all duration-300 ${
                  activeType === videoType
                    ? "bg-primary text-white border-primary shadow-lg shadow-primary/20"
                    : "bg-white text-text/50 border-gray-100 hover:border-primary/40 hover:text-primary"
                }`}
              >
                {VIDEO_TYPE_LABELS[videoType]}
              </button>
            ))}
          </div>
        )}

        {/* Video Tutorials Grid */}
        <div className="mb-20">
           <div className="flex items-center gap-4 mb-10">
              <h2 className="text-[12px] font-black text-title uppercase tracking-[0.4em]">
                {activeType === "TESTIMONIAL" ? "Carrousel Temoignages" : "Bibliothèque Vidéo"}
              </h2>
              <div className="h-px bg-gray-100 flex-1"></div>
           </div>

           {loading ? (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map(i => (
                  <div key={i} className="aspect-video bg-white/40 rounded-[2.5rem] animate-pulse"></div>
                ))}
             </div>
           ) : videoGrid.length === 0 ? (
             <div className="bg-white rounded-[3.5rem] p-20 text-center border border-gray-100 shadow-sm">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-200 text-3xl italic">?</div>
                <h3 className="text-xl font-black text-title uppercase mb-2">Aucune vidéo disponible</h3>
                <p className="text-sm text-text/30 font-bold uppercase tracking-widest">
                  {activeType === "TESTIMONIAL" ? "Aucun temoignage pour le moment." : "Le support vidéo sera bientôt disponible."}
                </p>
             </div>
           ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {videoGrid.map((video, idx) => (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -10 }}
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedVideo(video)}
                  >
                     <div className="relative aspect-video rounded-[2.5rem] overflow-hidden shadow-xl bg-white border-2 border-transparent group-hover:border-primary transition-all duration-500">
                        {video.thumbnail ? (
                          <img 
                            src={video.thumbnail} 
                            alt={video.title} 
                            className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                          />
                        ) : (
                          <>
                            {video.videoType === "TESTIMONIAL" ? (
                              <video
                                className="w-full h-full object-cover"
                                src={video.videoSrc || video.youtubeUrl}
                                preload="metadata"
                                muted
                              />
                            ) : (
                              <div className="w-full h-full bg-slate-50 flex items-center justify-center text-slate-100 italic text-4xl font-black">
                                SUPPORT
                              </div>
                            )}
                          </>
                        )}
                        
                        <div className="absolute inset-0 bg-gradient-to-t from-title/90 via-title/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
                        
                        {/* Play Pulse Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                           <div className="w-16 h-16 rounded-full bg-white text-primary flex items-center justify-center text-xl shadow-2xl scale-90 group-hover:scale-100 transition-transform duration-500 relative">
                              <FontAwesomeIcon icon={faPlayCircle} />
                              <div className="absolute inset-0 rounded-full bg-white opacity-40 animate-[ping_1.5s_infinite]"></div>
                           </div>
                        </div>

                        {/* Tag */}
                        <div className="absolute top-6 left-6 px-4 py-2 bg-white/20 backdrop-blur-md rounded-xl border border-white/20">
                           <span className="text-[9px] font-black text-white uppercase tracking-widest whitespace-nowrap">
                             {activeType === "TESTIMONIAL" ? "Temoignage Landing" : "Tutoriel Système"}
                           </span>
                        </div>
                     </div>

                     <div className="mt-6 px-2 flex items-start justify-between gap-4">
                        <div className="flex-1">
                           <h3 className="text-lg font-black text-title uppercase tracking-tighter leading-tight group-hover:text-primary transition-colors">{video.title}</h3>
                           <p className="text-[10px] font-bold text-text/30 uppercase tracking-widest mt-2 ml-1">Archive #882 / {new Date(video.createdAt).toLocaleDateString()}</p>
                        </div>
                        {isAdmin && (
                           <button 
                             onClick={(e) => { e.stopPropagation(); confirmDelete(video.id); }}
                             disabled={deletingId === video.id}
                             className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                           >
                              <FontAwesomeIcon icon={deletingId === video.id ? faSyncAlt : faTrash} className={deletingId === video.id ? 'animate-spin' : ''} />
                           </button>
                        )}
                     </div>
                  </motion.div>
                ))}
             </div>
           )}
        </div>

        {/* Contact Intelligence Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
           {/* Support Cards */}
           <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { 
                  icon: faEnvelope, 
                  title: 'Emailing Direct', 
                  val: 'contact@foknje7k.com', 
                  sub: 'Réponse sous 2h ouvrées',
                  color: 'bg-emerald-50 text-emerald-500',
                  link: 'mailto:contact@foknje7k.com'
                },
                { 
                  icon: faPhone, 
                  title: 'Support Téléphonique', 
                  val: '+216 51 347 528', 
                  sub: 'Ligne Directe Élite',
                  color: 'bg-blue-50 text-blue-500',
                  link: 'tel:+21651347528'
                }
              ].map((c, i) => (
                <motion.a
                  key={i}
                  href={c.link}
                  className="bg-white p-8 rounded-[3.5rem] border border-gray-100 shadow-sm group hover:shadow-2xl hover:border-primary transition-all duration-700"
                  whileHover={{ y: -5 }}
                >
                   <div className={`w-14 h-14 rounded-2xl ${c.color} flex items-center justify-center text-xl mb-6 shadow-lg group-hover:scale-110 transition-all duration-700`}>
                      <FontAwesomeIcon icon={c.icon} />
                   </div>
                   <p className="text-[11px] font-black text-text/30 uppercase tracking-[0.2em] mb-1">{c.title}</p>
                   <p className="text-xl font-black text-title tracking-tight">{c.val}</p>
                   <p className="text-[10px] font-bold text-text/20 uppercase tracking-widest mt-4 flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-primary"></div>
                      {c.sub}
                   </p>
                </motion.a>
              ))}
           </div>

           {/* Social Connect Card */}
           <div className="bg-[#0f172a] p-10 rounded-[4rem] relative overflow-hidden group shadow-2xl">
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 translate-x-1/2"></div>
              
              <div className="relative z-10 h-full flex flex-col justify-between">
                 <div>
                    <h4 className="text-[12px] font-black text-white uppercase tracking-[0.4em] mb-3">Rejoignez-nous</h4>
                    <p className="text-[11px] font-bold text-white/40 uppercase tracking-widest leading-relaxed">Suivez nos actualités en temps réel <br/> sur nos plateformes sociales.</p>
                 </div>

                 <div className="grid grid-cols-2 gap-4 mt-8">
                    {[
                      { icon: faFacebook, color: 'hover:bg-blue-600', link: 'https://www.facebook.com/people/%D9%81%D9%83-%D9%86%D8%AC%D8%A7%D8%AD%D9%83/100069589923551/' },
                      { icon: faInstagram, color: 'hover:bg-rose-500', link: 'https://www.instagram.com/fok.nje7ek/' },
                      { icon: faYoutube, color: 'hover:bg-red-600', link: 'https://www.youtube.com/@foknje7ik' },
                      { icon: faWhatsapp, color: 'hover:bg-emerald-500', link: 'https://wa.me/21651347528' }
                    ].map((s, i) => (
                      <a 
                        key={i} 
                        href={s.link} 
                        target="_blank" 
                        rel="noreferrer"
                        className={`h-16 rounded-2.5xl bg-white/5 border border-white/5 flex items-center justify-center text-white/40 text-2xl transition-all duration-500 ${s.color} hover:text-white hover:scale-105 hover:shadow-xl`}
                      >
                         <FontAwesomeIcon icon={s.icon} />
                      </a>
                    ))}
                 </div>
              </div>
           </div>
        </div>

        {/* Floating Add Video (Admin Only) */}
        {isAdmin && (
           <motion.div 
             initial={{ scale: 0 }}
             animate={{ scale: 1 }}
             className="fixed bottom-10 right-10 z-[100]"
           >
              <button
                onClick={() => setShowAddModal(true)}
                className="w-20 h-20 rounded-full bg-primary text-white shadow-[0_20px_50px_rgba(16,185,129,0.4)] flex items-center justify-center text-2xl hover:scale-110 active:scale-95 transition-all duration-500 relative group overflow-hidden"
              >
                 <div className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500 opacity-20"></div>
                 <FontAwesomeIcon icon={faPlus} />
                 <div className="absolute inset-0 rounded-full bg-primary animate-ping opacity-20 group-hover:opacity-0 transition-opacity"></div>
              </button>
           </motion.div>
        )}

      </div>

      <AnimatePresence>
        {!!selectedVideo && (
          <Modal
            open={!!selectedVideo}
            onClose={() => setSelectedVideo(null)}
            className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
            closeAfterTransition
          >
            <motion.div 
              className="w-full max-w-4xl bg-[#080C14] rounded-[3rem] shadow-3xl text-center outline-none border border-white/10 overflow-hidden"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="h-1 w-full bg-gradient-to-r from-emerald-500 via-primary to-blue-600"></div>
              
              <div className="p-6 md:p-10 text-left">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/10">
                      <FontAwesomeIcon icon={faPlayCircle} className="text-xl" />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight italic leading-none">{selectedVideo?.title}</h3>
                      <p className="text-[10px] font-black text-emerald-500/60 uppercase tracking-[0.4em] mt-2">Support Technique Elite</p>
                    </div>
                  </div>

                  <button 
                    onClick={() => setSelectedVideo(null)}
                    className="w-12 h-12 rounded-full bg-white/5 hover:bg-rose-500/20 hover:text-rose-500 transition-all flex items-center justify-center text-white/30 border border-white/5 group"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-lg group-hover:rotate-90 transition-transform" />
                  </button>
                </div>

                {/* Video Canvas */}
                <div className="relative group w-full">
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 to-primary/20 rounded-[2.5rem] blur opacity-40 group-hover:opacity-70 transition-opacity"></div>
                  <div className="relative aspect-video rounded-[2rem] md:rounded-[2.5rem] overflow-hidden bg-black border border-white/10 shadow-3xl">
                    {selectedVideo?.ytId ? (
                      <iframe
                        title={selectedVideo?.title}
                        src={`https://www.youtube.com/embed/${selectedVideo?.ytId}?autoplay=1&rel=0&modestbranding=1&showinfo=0`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full border-0"
                      />
                    ) : selectedVideo?.youtubeUrl ? (
                      <video
                        className="w-full h-full"
                        controls
                        autoPlay
                        preload="metadata"
                        src={buildStreamUrl(selectedVideo.youtubeUrl)}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-white/10">
                        <FontAwesomeIcon icon={faVideo} className="text-8xl mb-4" />
                        <p className="font-black uppercase tracking-widest text-sm">Source Indisponible</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Info Bar */}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/5 pt-8">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em]">Streaming Haute Définition Activé</span>
                  </div>
                  <a 
                    href={selectedVideo ? (selectedVideo.ytId ? selectedVideo.youtubeUrl : buildStreamUrl(selectedVideo.youtubeUrl)) : "#"} 
                    target="_blank" 
                    rel="noreferrer"
                    className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-[10px] font-black text-white/40 hover:text-white transition-all uppercase tracking-widest flex items-center gap-3 border border-white/5"
                  >
                    {selectedVideo?.ytId ? "Ouvrir dans YouTube" : "Ouvrir la video"} <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[8px]" />
                  </a>
                </div>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddModal && (
          <Modal
            open={showAddModal}
            onClose={() => setShowAddModal(false)}
            className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
            closeAfterTransition
          >
            <motion.div 
              className="w-full max-w-lg bg-[#05322B] rounded-[3rem] shadow-3xl outline-none border border-white/10 overflow-hidden"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
               <div className="relative p-8 lg:p-12 overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                  
                  <div className="flex items-center gap-4 mb-10 text-white relative z-10 text-left">
                     <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-emerald-400 text-xl shadow-xl backdrop-blur-md">
                        <FontAwesomeIcon icon={faVideo} />
                     </div>
                     <div>
                        <h3 className="text-2xl font-black uppercase tracking-tighter italic leading-none">
                          {activeType === "TESTIMONIAL" ? "Nouveau Temoignage" : "Nouveau Tutoriel"}
                        </h3>
                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mt-2">
                          {activeType === "TESTIMONIAL" ? "Carrousel Landing Page" : "Archive Système Elite"}
                        </p>
                     </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                     <div className="space-y-2 text-left">
                        <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-2">{activeType === "TESTIMONIAL" ? "Titre du temoignage" : "Titre du guide"}</label>
                        <TextField 
                          fullWidth 
                          value={form.title}
                          onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                          placeholder="Ex: Maîtriser le Terminal Dashboard"
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              bgcolor: 'rgba(255,255,255,0.03)', 
                              color: 'white', 
                              borderRadius: '1.5rem',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                              '&.Mui-focused fieldset': { borderColor: '#10b981' }
                            } 
                          }}
                        />
                     </div>

                     <div className="space-y-2 text-left">
                        {activeType === "SUPPORT" ? (
                          <>
                            <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-2">Lien YouTube source</label>
                            <TextField
                              fullWidth
                              value={form.youtubeUrl}
                              onChange={(e) => setForm((f) => ({ ...f, youtubeUrl: e.target.value }))}
                              placeholder="https://youtube.com/watch?v=..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'rgba(255,255,255,0.03)',
                                  color: 'white',
                                  borderRadius: '1.5rem',
                                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                  '&.Mui-focused fieldset': { borderColor: '#10b981' }
                                }
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-2">
                              Fichier video
                            </label>
                            <label className="block w-full cursor-pointer rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-[11px] font-black uppercase tracking-[0.15em] text-white/70 hover:border-emerald-400/40 transition-all">
                              {selectedVideoFile ? selectedVideoFile.name : "Choisir une video (tous formats video)"}
                              <input
                                type="file"
                                accept="video/*"
                                className="hidden"
                                onChange={(e) => setSelectedVideoFile(e.target.files?.[0] ?? null)}
                              />
                            </label>
                            {selectedVideoFile && (
                              <p className="text-[10px] font-bold text-emerald-300/80 uppercase tracking-widest ml-1">
                                {(selectedVideoFile.size / (1024 * 1024)).toFixed(2)} MB
                              </p>
                            )}
                          </>
                        )}
                     </div>

                     <div className="pt-6 flex gap-4">
                        <button 
                          type="button"
                          onClick={() => setShowAddModal(false)}
                          className="flex-1 h-14 rounded-2xl bg-white/5 text-white/40 font-black text-xs uppercase tracking-widest hover:bg-white/10 transition-all border border-white/5"
                        >
                           Annuler
                        </button>
                        <button 
                          type="submit"
                          disabled={saving}
                          className="flex-[2] h-14 rounded-2xl bg-emerald-500 text-white font-black text-xs uppercase tracking-widest shadow-xl shadow-emerald-500/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                        >
                           {saving ? "TRANSMISSION..." : "ENREGISTRER LA VIDÉO"}
                        </button>
                     </div>
                  </form>
               </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {openDeleteAlert && (
          <Modal
            open={openDeleteAlert}
            onClose={handleCloseDeleteAlert}
            className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
            closeAfterTransition
          >
            <motion.div 
              className="w-full max-w-[450px] bg-white rounded-[3rem] p-10 shadow-3xl text-center outline-none border border-white/20"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="w-24 h-24 rounded-[2.5rem] bg-rose-50 text-rose-500 flex items-center justify-center text-4xl mx-auto mb-8 border border-rose-100 shadow-sm relative">
                <div className="absolute inset-0 bg-rose-400/20 rounded-[2.5rem] animate-ping opacity-50"></div>
                <FontAwesomeIcon icon={faTrashCan} className="relative z-10" />
              </div>
              
              <h3 className="text-2xl font-black text-title uppercase tracking-tight mb-4">
                Supprimer le <span className="text-rose-500">{activeType === "TESTIMONIAL" ? "Temoignage" : "Tutoriel"}</span>
              </h3>
              
              <p className="text-sm font-medium text-text/60 leading-relaxed mb-10 px-4">
                {activeType === "TESTIMONIAL" ? "Êtes-vous sûr de vouloir supprimer ce témoignage ?" : "Êtes-vous sûr de vouloir supprimer ce tutoriel ?"} cette action est irréversible.
              </p>

              <div className="flex gap-4">
                <button
                  className="flex-1 h-16 bg-slate-50 text-text/40 font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-slate-100 transition-all duration-300 outline-none cursor-pointer"
                  onClick={handleCloseDeleteAlert}
                >
                  Annuler
                </button>
                <button
                  className="flex-1 h-16 bg-rose-500 text-white font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-xl shadow-rose-500/20 hover:bg-rose-600 hover:-translate-y-1 transition-all duration-300 outline-none cursor-pointer"
                  onClick={handleDelete}
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupportPage;

